import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AreaChart, BarChart, DonutChart } from '@tremor/react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import type { AnalyticsOverview, AnalyticsTrend } from '@octery/shared';

type Period = '7d' | '30d' | '90d' | 'all';
type Granularity = 'day' | 'week' | 'month';

function getPeriodDates(period: Period): { dateFrom?: string; granularity: Granularity } {
  const now = new Date();
  if (period === 'all') return { granularity: 'month' };
  const days = period === '7d' ? 7 : period === '30d' ? 30 : 90;
  const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  const granularity: Granularity = days <= 14 ? 'day' : days <= 60 ? 'week' : 'month';
  return { dateFrom: from.toISOString(), granularity };
}

interface ConversionFunnel {
  total: number;
  completed: number;
  consultations: number;
  handoffs: number;
  abandoned: number;
  active: number;
}

export function DashboardPage() {
  const [period, setPeriod] = useState<Period>('30d');
  const periodConfig = getPeriodDates(period);

  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['analytics', 'overview'],
    queryFn: () => api.get<AnalyticsOverview>('/api/analytics/overview'),
  });

  const { data: trends = [], isLoading: trendsLoading } = useQuery({
    queryKey: ['analytics', 'trends', period],
    queryFn: () => {
      const params = new URLSearchParams();
      if (periodConfig.dateFrom) params.set('dateFrom', periodConfig.dateFrom);
      params.set('granularity', periodConfig.granularity);
      return api.get<AnalyticsTrend[]>(`/api/analytics/trends?${params.toString()}`);
    },
  });

  const { data: funnel } = useQuery({
    queryKey: ['analytics', 'conversions'],
    queryFn: () => api.get<ConversionFunnel>('/api/analytics/conversions'),
  });

  const stats = [
    {
      title: 'Total Conversations',
      value: overview?.totalConversations ?? 0,
      description: 'All time',
      icon: '💬',
    },
    {
      title: 'Total Messages',
      value: overview?.totalMessages ?? 0,
      description: 'All time',
      icon: '📝',
    },
    {
      title: 'Avg Messages / Conv.',
      value: (overview?.avgMessagesPerConversation ?? 0).toFixed(1),
      description: 'Per conversation',
      icon: '📊',
    },
    {
      title: 'Consultation Rate',
      value: `${(overview?.consultationRate ?? 0).toFixed(1)}%`,
      description: 'Offered consultation',
      icon: '📋',
    },
    {
      title: 'Manager Handoff',
      value: `${(overview?.managerHandoffRate ?? 0).toFixed(1)}%`,
      description: 'Transferred to manager',
      icon: '🙋',
    },
    {
      title: 'Avg First Response',
      value: `${(overview?.avgFirstResponseTime ?? 0).toFixed(0)}s`,
      description: 'Time to first AI response',
      icon: '⏱️',
    },
  ];

  // Donut chart data for funnel
  const donutData = funnel
    ? [
        { name: 'Completed', value: funnel.completed },
        { name: 'Consultations', value: funnel.consultations },
        { name: 'Handoffs', value: funnel.handoffs },
        { name: 'Abandoned', value: funnel.abandoned },
        { name: 'Active', value: funnel.active },
      ].filter((d) => d.value > 0)
    : [];

  return (
    <div className="space-y-6">
      {/* Header + period filter */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground">Overview of your AI intake performance</p>
        </div>
        <div className="flex gap-1 bg-muted p-1 rounded-lg">
          {(['7d', '30d', '90d', 'all'] as Period[]).map((p) => (
            <Button
              key={p}
              variant={period === p ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setPeriod(p)}
            >
              {p === 'all' ? 'All' : p}
            </Button>
          ))}
        </div>
      </div>

      {/* Stats cards */}
      {overviewLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-2">
                <div className="h-4 bg-muted rounded w-1/2" />
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted rounded w-1/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {stats.map((stat) => (
            <Card key={stat.title}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {stat.title}
                </CardTitle>
                <span className="text-lg">{stat.icon}</span>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
                <p className="text-xs text-muted-foreground">{stat.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Charts row */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Conversations trend — spans 2 cols */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Conversations Over Time</CardTitle>
            <CardDescription>
              Daily conversation volume and engagement metrics
            </CardDescription>
          </CardHeader>
          <CardContent>
            {trendsLoading ? (
              <div className="h-[300px] bg-muted/50 rounded animate-pulse" />
            ) : trends.length === 0 ? (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground text-sm">
                No data for the selected period
              </div>
            ) : (
              <AreaChart
                className="h-[300px]"
                data={trends}
                index="date"
                categories={['conversations', 'consultations', 'handoffs']}
                colors={['blue', 'emerald', 'violet']}
                valueFormatter={(n) => String(n)}
                showAnimation
                showLegend
                curveType="monotone"
              />
            )}
          </CardContent>
        </Card>

        {/* Conversions funnel donut */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Conversions</CardTitle>
            <CardDescription>Outcome distribution</CardDescription>
          </CardHeader>
          <CardContent>
            {!funnel ? (
              <div className="h-[300px] bg-muted/50 rounded animate-pulse" />
            ) : donutData.length === 0 ? (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground text-sm">
                No data yet
              </div>
            ) : (
              <DonutChart
                className="h-[250px]"
                data={donutData}
                category="value"
                index="name"
                colors={['emerald', 'blue', 'violet', 'gray', 'cyan']}
                valueFormatter={(n) => String(n)}
                showAnimation
              />
            )}
            {funnel && funnel.total > 0 && (
              <div className="mt-3 text-center text-xs text-muted-foreground">
                Total: {funnel.total} conversations
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Messages bar chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Messages Volume</CardTitle>
          <CardDescription>Total messages exchanged over time</CardDescription>
        </CardHeader>
        <CardContent>
          {trendsLoading ? (
            <div className="h-[250px] bg-muted/50 rounded animate-pulse" />
          ) : trends.length === 0 ? (
            <div className="h-[250px] flex items-center justify-center text-muted-foreground text-sm">
              No data for the selected period
            </div>
          ) : (
            <BarChart
              className="h-[250px]"
              data={trends}
              index="date"
              categories={['messages']}
              colors={['blue']}
              valueFormatter={(n) => String(n)}
              showAnimation
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
